import { useState } from 'react';

const PHONE = '561-445-3948';
const FORM_URL = 'https://readdy.ai/api/form/d70pu4f9hgr6oot2r4c0';

const serviceOptions = [
  'Pressure Cleaning',
  'Roof Cleaning',
  'Driveway Cleaning',
  'Screen Patio Cleaning',
  'Gutter Cleaning',
  'Multiple Services',
];

export default function MidPageCTA() {
  const [formData, setFormData] = useState({ name: '', phone: '', service: '' });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!formData.name || !formData.phone || !formData.service) return;
    setStatus('submitting');
    try {
      const body = new URLSearchParams({
        name: formData.name,
        phone: formData.phone,
        service: formData.service,
      });
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setStatus('success');
        setFormData({ name: '', phone: '', service: '' });
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <section id="contact" className="bg-gradient-to-br from-teal-50 to-slate-100 py-16 lg:py-24">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white border border-gray-200 rounded-3xl overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-5">
            {/* Left: Form */}
            <div className="lg:col-span-3 p-8 lg:p-10">
              <span className="inline-block bg-orange-100 text-orange-600 text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full mb-4">
                Free Estimate
              </span>
              <h2 className="text-2xl sm:text-3xl font-black text-slate-900 mb-2">
                Ready To Transform Your Property?
              </h2>
              <p className="text-slate-500 text-sm mb-7">
                Get your free, no-obligation estimate. We respond same day.
              </p>

              {status === 'success' ? (
                <div className="bg-teal-50 border border-teal-200 rounded-xl p-6 text-center">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-teal-100 mx-auto mb-3">
                    <i className="ri-check-double-line text-teal-600 text-xl"></i>
                  </div>
                  <p className="text-teal-800 font-bold text-lg mb-1">Request Sent!</p>
                  <p className="text-teal-600 text-sm">We'll reach out within a few hours. Check your phone!</p>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  data-readdy-form
                  id="mid-estimate-form"
                  className="space-y-4"
                >
                  <div>
                    <label className="block text-slate-700 text-sm font-semibold mb-1.5" htmlFor="mid-name">
                      Your Name
                    </label>
                    <input
                      id="mid-name"
                      name="name"
                      type="text"
                      required
                      placeholder="John Smith"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm text-slate-800 focus:outline-none focus:border-teal-500 placeholder:text-slate-400"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-700 text-sm font-semibold mb-1.5" htmlFor="mid-phone">
                      Phone Number
                    </label>
                    <input
                      id="mid-phone"
                      name="phone"
                      type="tel"
                      required
                      placeholder="(561) 000-0000"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm text-slate-800 focus:outline-none focus:border-teal-500 placeholder:text-slate-400"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-700 text-sm font-semibold mb-1.5" htmlFor="mid-service">
                      Service Needed
                    </label>
                    <select
                      id="mid-service"
                      name="service"
                      required
                      value={formData.service}
                      onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm text-slate-800 focus:outline-none focus:border-teal-500"
                    >
                      <option value="">Select a service...</option>
                      {serviceOptions.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                  {status === 'error' && (
                    <p className="text-red-600 text-xs">Something went wrong. Please try again or call us directly.</p>
                  )}
                  <button
                    type="submit"
                    disabled={status === 'submitting'}
                    className="w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-black text-base py-4 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {status === 'submitting' ? 'Sending...' : 'Get My Free Estimate →'}
                  </button>
                  <p className="text-slate-400 text-xs text-center">No obligation. Fast response. Local team.</p>
                </form>
              )}
            </div>

            {/* Right: Phone + Trust */}
            <div className="lg:col-span-2 bg-teal-700 p-8 lg:p-10 flex flex-col justify-center">
              <p className="text-teal-200 text-sm font-medium mb-2">Or call us directly</p>
              <a
                href={`tel:${PHONE}`}
                className="block text-white font-black text-3xl lg:text-4xl leading-tight mb-1 cursor-pointer hover:text-teal-200 transition-colors"
              >
                {PHONE}
              </a>
              <p className="text-teal-300 text-sm mb-8">Mon–Sat: 7AM – 6PM</p>

              <div className="space-y-4">
                {[
                  { icon: 'ri-time-fill', text: 'Same-day response guaranteed' },
                  { icon: 'ri-price-tag-3-line', text: 'Free, no-obligation quotes' },
                  { icon: 'ri-map-pin-fill', text: 'Serving all of South Florida' },
                  { icon: 'ri-shield-check-fill', text: 'Licensed & insured team' },
                ].map((item) => (
                  <div key={item.text} className="flex items-center gap-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-teal-600">
                      <i className={`${item.icon} text-teal-200 text-base`}></i>
                    </div>
                    <p className="text-teal-100 text-sm font-medium">{item.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
